import {
	createTheme,
	PaletteOptions,
	ThemeOptions,
	useTheme,
} from "@mui/material";

import { TypographyOptions } from "@mui/material/styles/createTypography";

import {
	DEFAULT_TERTIARY_COLOR,
	DEFAULT_SURFACE_TINT,
	DEFAULT_SUCCESS_LIGHT,
	DEFAULT_SUCCESS_DARK,
	DEFAULT_BRAND_FAINT,
	DEFAULT_SURFACE_TWO,
	DEFAULT_DIVIDER,
	DEFAULT_TEXT_PRIMARY,
	DEFAULT_TEXT_SECONDARY,
	DEFAULT_WARNING_LIGHT,
	DEFAULT_WARNING_DARK,
	lighten,
	darken,
} from "./colorUtils";

type CustomPaletteOptions = {
	main: string;
	main2?: string;
	main3?: string;
	light: string;
	dark: string;
	light2?: string;
	dark2?: string;
	light3?: string;
	dark3?: string;
	light4?: string;
	dark4?: string;
};

declare module "@mui/material/styles/createPalette" {
	interface Palette {
		surface: CustomPaletteOptions;
		tertiary: CustomPaletteOptions;
		surfaceTint: CustomPaletteOptions;
		brandFaint: CustomPaletteOptions;
		surfaceTwo: CustomPaletteOptions;
		accent: CustomPaletteOptions;
		icon: CustomPaletteOptions;
		outline: CustomPaletteOptions;
		menu: CustomPaletteOptions;
		textMain: CustomPaletteOptions;
		textPrimary: CustomPaletteOptions;
		textSecondary: CustomPaletteOptions;
		textLinkHelpSupport: CustomPaletteOptions;
	}
	interface PaletteOptions {
		surface: CustomPaletteOptions;
		tertiary: CustomPaletteOptions;
		surfaceTint: CustomPaletteOptions;
		brandFaint: CustomPaletteOptions;
		surfaceTwo: CustomPaletteOptions;
		accent: CustomPaletteOptions;
		icon: CustomPaletteOptions;
		outline: CustomPaletteOptions;
		menu: CustomPaletteOptions;
		textMain: CustomPaletteOptions;
		textPrimary: CustomPaletteOptions;
		textSecondary: CustomPaletteOptions;
		textLinkHelpSupport: CustomPaletteOptions;
	}
}

export interface ExtendedPaletteOptions extends PaletteOptions {
	surface: CustomPaletteOptions;
	tertiary: CustomPaletteOptions;
	surfaceTint: CustomPaletteOptions;
	brandFaint: CustomPaletteOptions;
	surfaceTwo: CustomPaletteOptions;
	accent: CustomPaletteOptions;
	icon: CustomPaletteOptions;
	outline: CustomPaletteOptions;
	menu: CustomPaletteOptions;
	textMain: CustomPaletteOptions;
	textPrimary: CustomPaletteOptions;
	textSecondary: CustomPaletteOptions;
	textLinkHelpSupport: CustomPaletteOptions;
}

declare module "@mui/material/Typography" {
	interface TypographyPropsVariantOverrides {
		display: true;
		headlineLarge: true;
		headlineMedium: true;
		headlineSmall: true;
		inputHelper: true;
		titleMedium: true;
		titleSmall: true;
		bodyLarge: true;
		bodyLargeBold: true;
		bodyMedium: true;
		bodySmall: true;
		labelLarge: true;
		labelMedium: true;
		labelSmall: true;
		titleTableHeader: true;
		bodyOptionHelp: true;
		titleSmallOptionHelp: true;
	}
}

export interface ExtendedTypographyOptions extends TypographyOptions {
	display?: React.CSSProperties;
	headlineLarge?: React.CSSProperties;
	headlineMedium?: React.CSSProperties;
	headlineSmall?: React.CSSProperties;
	titleMedium?: React.CSSProperties;
	titleSmall?: React.CSSProperties;
	bodyLarge?: React.CSSProperties;
	bodyLargeBold?: React.CSSProperties;
	bodyMedium?: React.CSSProperties;
	bodySmall?: React.CSSProperties;
	labelLarge?: React.CSSProperties;
	labelMedium?: React.CSSProperties;
	labelSmall?: React.CSSProperties;
	titleTableHeader?: React.CSSProperties;
	bodyOptionHelp?: React.CSSProperties;
	titleSmallOptionHelp?: React.CSSProperties;
}

export const themePalette = {
	mode: "light",
	primary: {
		main: "#6F3996",
		light: "#BAA3CA",
		dark: "#52197B",
	},
	secondary: {
		main: "#4CB7A4",
		light: "#E7EFB8",
		dark: "#005757",
	},
	tertiary: {
		main: DEFAULT_TERTIARY_COLOR,
		light: lighten(DEFAULT_TERTIARY_COLOR, 12),
		dark: darken(DEFAULT_TERTIARY_COLOR, 12),
	},
	surfaceTint: {
		main: DEFAULT_SURFACE_TINT,
		light: lighten(DEFAULT_SURFACE_TINT, 4),
		dark: darken(DEFAULT_SURFACE_TINT, 6),
	},
	// SIMS-8458 — the static fallback palette needs these too.
	//
	// createDynamicTheme() only calls generatePalette when the account HAS theme settings;
	// with none (a fresh account, or an override reset to the global default) it returns
	// this object verbatim. Before this, that path produced MUI's stock green for
	// `success`, a translucent `divider`, and — because nothing else defines it —
	// `brandFaint: undefined`, which DailyJourneyTab reads as `brandFaint.main` for the
	// selected day card.
	//
	// So the roles are defined here as well, from the same DEFAULT_* constants, and
	// __tests__/utils/themeSettingsChain.test.ts asserts the two palettes agree.
	success: {
		main: DEFAULT_SUCCESS_DARK,
		light: DEFAULT_SUCCESS_LIGHT,
		dark: DEFAULT_SUCCESS_DARK,
	},
	brandFaint: {
		main: DEFAULT_BRAND_FAINT,
		light: DEFAULT_BRAND_FAINT,
		dark: DEFAULT_BRAND_FAINT,
	},
	surfaceTwo: {
		main: DEFAULT_SURFACE_TWO,
		light: DEFAULT_SURFACE_TWO,
		dark: DEFAULT_SURFACE_TWO,
	},
	divider: DEFAULT_DIVIDER,
	text: {
		primary: DEFAULT_TEXT_PRIMARY,
		secondary: DEFAULT_TEXT_SECONDARY,
	},
	error: {
		main: "#AB0707",
		light: "#FCD6D0",
		dark: "#AB0707",
	},
	warning: {
		main: "#F8955B",
		light: DEFAULT_WARNING_LIGHT,
		dark: DEFAULT_WARNING_DARK,
	},
	surface: {
		main: "#F7F4F9",
		light: "#FFFFFF",
		dark: "#E9D0F4",
		dark2: "#ADA6B3",
	},
	accent: {
		main: "#52197B",
	},
	icon: {
		main: "#ADA6B3",
		light: "#D9D1DD",
		dark: "#4C4C56",
		dark2: "#52197B",
	},
	outline: {
		main: "#D9D1DD",
		light: "#E4DEE9",
		dark: "#ADA6B3",
		dark2: "#4C4C56",
	},
	menu: {
		main: "#52197B",
		light: "#8A54B2",
		light2: "#6F3996",
	},
	textMain: {
		main: "#E5E6E6",
		main2: "#79797D",
		main3: "#BAA3CA",
		light: "#FFFFFF",
		dark: "#CCCCCE",
		light2: "#FCFCFC",
		dark2: "#1F2026",
		light3: "#F8F8F8",
		dark3: "#F26716",
		light4: "#F5F5F5",
		dark4: "#005757",
	},
	textPrimary: {
		main: "#1F2026",
		light: "#1F2026",
	},
	textSecondary: {
		main: "#79797D",
		light: "#BAA3CA",
	},
	textLinkHelpSupport: {
		main: "#3a6364",
	},
};
export const themeTypography = {
	display: {
		fontWeight: "900",
		fontSize: "4rem",
		lineHeight: "5.5rem",
	},
	headlineLarge: {
		fontWeight: "bold",
		fontSize: "3rem",
		lineHeight: "4rem",
	},
	headlineMedium: {
		fontWeight: "bold",
		fontSize: "2rem",
		lineHeight: "2.5rem",
	},
	headlineSmall: {
		fontWeight: "bold",
		fontSize: "1.5rem",
		lineHeight: "2rem",
	},
	inputHelper: {
		fontSize: "12px",
		fontStyle: "normal",
		fontWeight: "400",
		lineHeight: "166%" /* 19.92px */,
		letterSpacing: "0.4px",
	},
	titleMedium: {
		fontWeight: "900",
		fontSize: "1.25rem", // 20px
		lineHeight: "1.5rem",
	},
	titleSmall: {
		fontWeight: "700",
		fontSize: "18px",
		lineHeight: "1.25rem",
	},
	titleTableHeader: {
		fontWeight: "600",
		fontSize: "14px",
		lineHeight: "1.5rem",
	},
	titleSmallOptionHelp: {
		fontWeight: "700",
		fontSize: "18px",
		lineHeight: "1.5rem",
	},
	bodyLarge: {
		fontWeight: "normal",
		fontSize: "1rem", //16px
		lineHeight: "1.5rem",
	},
	bodyLargeBold: {
		fontWeight: "bold",
		fontSize: "1rem", //16px
		lineHeight: "1.5rem",
	},
	bodyMedium: {
		fontWeight: "normal",
		fontSize: "0.875rem",
		lineHeight: "1.25rem",
	},
	bodySmall: {
		fontWeight: "normal",
		fontSize: "0.75rem",
		lineHeight: "1rem",
	},
	bodyOptionHelp: {
		fontWeight: "normal",
		fontSize: "1rem",
		lineHeight: "1.5rem",
	},
	labelLarge: {
		fontWeight: "300",
		fontSize: "1rem",
		lineHeight: "1.5rem",
	},
	labelMedium: {
		fontWeight: "300",
		fontSize: "0.75rem",
		lineHeight: "1.5rem",
	},
	labelSmall: {
		fontWeight: "300",
		fontSize: "11px",
		lineHeight: "1rem",
	},
};

export const defaultPalette = themePalette;

export interface ThemeColorSettings {
	primaryColor?: string | null;
	secondaryColor?: string | null;
	surfaceColor?: string | null;
	surfaceLight?: string | null;
	surfaceDark?: string | null;
	primaryDark?: string | null;
	secondaryLight?: string | null;
	outlineMain?: string | null;
	outlineDark?: string | null;
	textMainDark2?: string | null;
	textMainMain2?: string | null;
	textSecondaryMain?: string | null;
	tertiaryColor?: string | null;
	surfaceTint?: string | null;
	// SIMS-8458 AC7/AC8 — the design-system roles this story exposes in the colour
	// settings screen. Must stay in step with ThemeColorsConfig in colorUtils.ts and with
	// the mapping in createDynamicTheme below; a key present in one and missing from
	// another is dropped silently rather than failing loudly.
	successLight?: string | null;
	successDark?: string | null;
	brandFaint?: string | null;
	dividerColor?: string | null;
	surfaceTwo?: string | null;
	textPrimaryMain?: string | null;
	fontFamily?: string | null;
}

/**
 * Creates a dynamic MUI theme from account theme color settings.
 * Falls back to the default hardcoded palette when no colors are provided.
 *
 * Supports:
 *  - primaryColor: navigation, menu, buttons, accent (#276048 for B2D)
 *  - secondaryColor: secondary actions, badges (#70A815 for B2D)
 *  - surfaceColor: backgrounds, cards (#DEE7E7 for B2D)
 *  - fontFamily: font across the UI (Roboto by default)
 */
export function createDynamicTheme(settings?: ThemeColorSettings | null) {
	if (settings?.primaryColor || settings?.secondaryColor) {
		// Lazy import to avoid circular deps at module level
		// eslint-disable-next-line @typescript-eslint/no-var-requires
		const { generatePalette } = require("./colorUtils");
		const dynamicPalette = generatePalette({
			primaryColor: settings.primaryColor || defaultPalette.primary.dark,
			secondaryColor: settings.secondaryColor || defaultPalette.secondary.main,
			surfaceColor: settings.surfaceColor || undefined,
			surfaceLight: settings.surfaceLight || undefined,
			surfaceDark: settings.surfaceDark || undefined,
			primaryDark: settings.primaryDark || undefined,
			secondaryLight: settings.secondaryLight || undefined,
			outlineMain: settings.outlineMain || undefined,
			outlineDark: settings.outlineDark || undefined,
			textMainDark2: settings.textMainDark2 || undefined,
			textMainMain2: settings.textMainMain2 || undefined,
			textSecondaryMain: settings.textSecondaryMain || undefined,
			tertiaryColor: settings.tertiaryColor || undefined,
			surfaceTint: settings.surfaceTint || undefined,
			// SIMS-8458 AC8 — the roles this story added. This mapping is hand-written per
			// key, so a role missing here is silently dropped between the saved setting and
			// the palette: the settings screen saves it, generatePalette never sees it, and
			// the override appears not to work.
			successLight: settings.successLight || undefined,
			successDark: settings.successDark || undefined,
			brandFaint: settings.brandFaint || undefined,
			dividerColor: settings.dividerColor || undefined,
			surfaceTwo: settings.surfaceTwo || undefined,
			textPrimaryMain: settings.textPrimaryMain || undefined,
			fontFamily: settings.fontFamily || undefined,
		});

		// Build typography with optional font family override
		const typography = settings.fontFamily
			? {
					...themeTypography,
					fontFamily: `"${settings.fontFamily}", "Helvetica", "Arial", sans-serif`,
				}
			: themeTypography;

		return createTheme({
			palette: dynamicPalette as ExtendedPaletteOptions,
			typography: typography as ExtendedTypographyOptions,
		} as ThemeOptions);
	}

	return createTheme({
		palette: themePalette as ExtendedPaletteOptions,
		typography: themeTypography as ExtendedTypographyOptions,
	} as ThemeOptions);
}

const theme = createDynamicTheme();

export default theme;

export type AppTheme = typeof theme;

export const useAppTheme = () => useTheme<AppTheme>();
